import React, { useState } from 'react';
import { Pressable, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';
import { IconAlertTriangleFilled, IconInfoCircle } from '@tabler/icons-react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type FieldSize = 'default' | 'small';
export type FieldState = 'enabled' | 'hover' | 'active' | 'focus' | 'disabled' | 'readOnly';
export type FieldValidation = 'none' | 'error';
export type FieldIconProps = { size: number; color: string };
export type FieldIconSlot = React.ReactNode | ((props: FieldIconProps) => React.ReactNode);

export function FieldIcon({ icon, ...props }: FieldIconProps & { icon: FieldIconSlot }) {
  return (
    <Box accessible={false} pointerEvents="none" alignItems="center" justifyContent="center">
      {typeof icon === 'function'
        ? icon(props)
        : React.isValidElement<FieldIconProps>(icon)
          ? React.cloneElement(icon, props)
          : icon}
    </Box>
  );
}

interface FieldOptions {
  size?: FieldSize;
  state?: FieldState;
  disabled?: boolean;
  readOnly?: boolean;
  validation?: FieldValidation;
}

export function useField(props: FieldOptions) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  return { ...getFieldPresentation(theme, props, focused, hovered), setFocused, setHovered };
}

function getFieldPresentation(
  theme: ReturnType<typeof useTheme>,
  { size = 'default', state = 'enabled', disabled = false, readOnly = false, validation = 'none' }: FieldOptions,
  focused: boolean,
  hovered: boolean,
) {
  const isDisabled = disabled || state === 'disabled';
  const isReadOnly = readOnly || state === 'readOnly';
  const interactive = !isDisabled && !isReadOnly;
  const active = interactive && (focused || state === 'active' || state === 'focus');
  const emphasized = active || (interactive && (hovered || state === 'hover'));
  const error = validation === 'error';
  const small = size === 'small';
  return {
    theme,
    small,
    isDisabled,
    isReadOnly,
    interactive,
    active,
    error,
    // The Figma field heights use the general sizing scale (40 / 48).
    height: Number(theme.tokens.sizing[small ? 'sizing.general.x10' : 'sizing.general.x12']),
    iconSize: Number(theme.tokens.sizing[small ? 'sizing.icon.sm' : 'sizing.icon.default']),
    actionSize: Number(theme.tokens.sizing[small ? 'sizing.general.x6' : 'sizing.general.x8']),
    buttonSize: Number(theme.tokens.sizing[small ? 'sizing.general.x8' : 'sizing.general.x10']),
    iconColor: theme.colors[isDisabled ? 'icon.disabled' : 'icon.secondary'],
    textColor: theme.colors[isDisabled ? 'text.disabled' : 'text.primary'],
    borderColor:
      theme.colors[
        isDisabled ? 'border.disabled' : error ? 'border.error' : emphasized ? 'border.focused' : 'border.secondary'
      ],
    borderWidth:
      theme.borderWidths[!isDisabled && (error || emphasized) ? 'border-width.emphasized' : 'border-width.standard'],
  };
}

export type FieldPresentation = ReturnType<typeof useField>;

export function FieldBorder({ field }: { field: FieldPresentation }) {
  return (
    <Box
      pointerEvents="none"
      borderRadius="radius.component.fields.input"
      style={[StyleSheet.absoluteFill, { borderColor: field.borderColor, borderWidth: field.borderWidth }]}
    />
  );
}

export function FieldAction({
  icon,
  accessibilityLabel,
  onPress,
  disabled = false,
  size,
  iconSize,
  primary = false,
}: {
  icon: FieldIconSlot;
  accessibilityLabel: string;
  onPress?: () => void;
  disabled?: boolean;
  size: number;
  iconSize: number;
  primary?: boolean;
}) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={(event) => {
        event.stopPropagation();
        onPress?.();
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      hitSlop={4}
      style={({ pressed }) => ({
        width: size,
        height: size,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.borderRadii['radius.full'],
        backgroundColor: primary
          ? theme.colors['component.button.bg.primary']
          : pressed
            ? theme.colors['state-layer.press']
            : hovered
              ? theme.colors['state-layer.hover']
              : 'transparent',
        borderWidth: focused && !disabled ? theme.borderWidths['border-width.emphasized'] : 0,
        borderColor: theme.colors['border.focused'],
        opacity: primary && disabled ? 0.4 : 1,
      })}
    >
      <FieldIcon
        icon={icon}
        size={iconSize}
        color={theme.colors[primary ? 'text.inverse.primary' : disabled ? 'icon.disabled' : 'icon.primary']}
      />
    </Pressable>
  );
}

export interface FieldFrameProps {
  label?: string;
  helperText?: string;
  errorMessage?: string;
  showInfoIcon?: boolean;
  onInfoPress?: () => void;
  infoAccessibilityLabel?: string;
  containerStyle?: StyleProp<ViewStyle>;
}

export function FieldFrame({
  children,
  field,
  label,
  helperText,
  errorMessage,
  showInfoIcon,
  onInfoPress,
  infoAccessibilityLabel = 'More information',
  labelId,
  descriptionId,
  containerStyle,
}: FieldFrameProps & {
  children: React.ReactNode;
  field: FieldPresentation;
  labelId: string;
  descriptionId: string;
}) {
  const { theme, isDisabled, error } = field;
  const description = error ? errorMessage : helperText;
  return (
    <Box gap="spacing.general.x2" style={containerStyle}>
      <FieldLabel
        field={field}
        label={label}
        labelId={labelId}
        showInfoIcon={showInfoIcon}
        onInfoPress={onInfoPress}
        infoAccessibilityLabel={infoAccessibilityLabel}
      />
      {children}
      {description ? (
        <Box flexDirection="row" alignItems="flex-start" gap="spacing.general.x1">
          {error ? (
            <Box minHeight={theme.textVariants.bodySmall.lineHeight} justifyContent="center">
              <IconAlertTriangleFilled
                size={Number(theme.tokens.sizing['sizing.icon.s'])}
                color={theme.colors['icon.error']}
              />
            </Box>
          ) : null}
          <Text
            nativeID={descriptionId}
            accessibilityLiveRegion={error ? 'polite' : 'none'}
            variant="bodySmall"
            flex={1}
            color={error ? 'text.error' : isDisabled ? 'text.disabled' : 'text.secondary'}
          >
            {description}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

function FieldLabel({
  field,
  label,
  labelId,
  showInfoIcon,
  onInfoPress,
  infoAccessibilityLabel = 'More information',
}: FieldFrameProps & { field: FieldPresentation; labelId: string }) {
  const { theme, isDisabled } = field;
  const infoSize = Number(theme.tokens.sizing['sizing.icon.sm']);
  return (
    <>
      {label || showInfoIcon ? (
        <Box flexDirection="row" alignItems="center" gap="spacing.general.x1">
          {label ? (
            <Text
              nativeID={labelId}
              variant="bodyBase"
              flexShrink={1}
              color={isDisabled ? 'text.disabled' : 'text.primary'}
            >
              {label}
            </Text>
          ) : null}
          {showInfoIcon ? (
            onInfoPress ? (
              <FieldAction
                icon={<IconInfoCircle strokeWidth={2} />}
                size={infoSize}
                iconSize={infoSize}
                accessibilityLabel={infoAccessibilityLabel}
                onPress={onInfoPress}
                disabled={isDisabled}
              />
            ) : (
              <FieldIcon icon={<IconInfoCircle strokeWidth={2} />} size={infoSize} color={field.iconColor} />
            )
          ) : null}
        </Box>
      ) : null}
    </>
  );
}
